/**
 * ScreenRecorderWidget — iter1023
 *
 * In-browser screen recording using the native
 * `navigator.mediaDevices.getDisplayMedia()` + MediaRecorder APIs.
 *
 * • Пациентът избира кой прозорец/tab да записва (browser prompt)
 * • WebM запис на устройството
 * • Downloadable файл
 * • Работи в Chrome, Edge, Firefox (Safari има ограничения)
 * • Никакъв backend upload — всичко остава локално (GDPR-friendly)
 */
import React, { useEffect, useRef, useState } from "react";
import { Camera, StopCircle, Download, X, AlertTriangle, Mic, MicOff } from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";
import { toast } from "sonner";

function fmtDuration(secs) {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export default function ScreenRecorderWidget() {
  const { isDark } = useTheme();
  const { language } = useAuth();
  const bg = (language || "bg") === "bg";

  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [withMic, setWithMic] = useState(false);
  const [withMusic, setWithMusic] = useState(false);   // iter1027 — background music
  const [duration, setDuration] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadName, setDownloadName] = useState("");
  const [error, setError] = useState("");

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const micStreamRef = useRef(null);
  const musicCtxRef = useRef(null);   // iter1027 — WebAudio context for music
  const musicStopRef = useRef(null);
  const startTimeRef = useRef(0);
  const tickRef = useRef(null);

  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (micStreamRef.current) micStreamRef.current.getTracks().forEach((t) => t.stop());
      if (tickRef.current) clearInterval(tickRef.current);
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSupported =
    typeof navigator !== "undefined"
    && navigator.mediaDevices
    && typeof navigator.mediaDevices.getDisplayMedia === "function"
    && typeof window.MediaRecorder !== "undefined";

  async function startRecording() {
    setError("");
    setDownloadUrl(null);
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: true,
      });
      let combined = displayStream;
      let micStream = null;

      // iter1027 — Optional soft ambient music via WebAudio. A calm
      // 4-note pentatonic loop that layers behind the recording.
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const dest = audioCtx.createMediaStreamDestination();
      let anyExtraAudio = false;

      if (withMic) {
        try {
          micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          audioCtx.createMediaStreamSource(micStream).connect(dest);
          micStreamRef.current = micStream;
          anyExtraAudio = true;
        } catch {
          toast.warning(bg ? "Микрофон не е разрешен — записваме без него" : "Mic denied — recording without it");
        }
      }
      if (withMusic) {
        // Sample library-free procedural music (no external file).
        // Pentatonic (C4, E4, G4, A4) at ~65 BPM with gentle 12 dB gain roll-off.
        const notes = [261.63, 329.63, 392.0, 440.0];
        const beat = 0.9;    // sec per note
        const master = audioCtx.createGain();
        master.gain.value = 0.06;   // low volume so voice/screen sound isn't drowned
        // Soft filter for warmth
        const filter = audioCtx.createBiquadFilter();
        filter.type = "lowpass";
        filter.frequency.value = 1400;
        master.connect(filter);
        filter.connect(dest);

        let idx = 0;
        let stopped = false;
        const schedule = () => {
          if (stopped) return;
          const now = audioCtx.currentTime;
          const freq = notes[idx % notes.length];
          const osc = audioCtx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = freq;
          const g = audioCtx.createGain();
          g.gain.setValueAtTime(0, now);
          g.gain.linearRampToValueAtTime(0.5, now + 0.15);
          g.gain.linearRampToValueAtTime(0.0, now + beat * 1.1);
          osc.connect(g).connect(master);
          osc.start(now);
          osc.stop(now + beat * 1.2);
          idx = (idx + 1) % notes.length;
          setTimeout(schedule, beat * 1000);
        };
        schedule();
        musicCtxRef.current = audioCtx;
        musicStopRef.current = () => { stopped = true; };
        anyExtraAudio = true;
      }

      // System audio if present — always mixed via WebAudio if we have extras.
      if (anyExtraAudio && displayStream.getAudioTracks().length > 0) {
        audioCtx.createMediaStreamSource(new MediaStream([displayStream.getAudioTracks()[0]])).connect(dest);
      }
      if (anyExtraAudio) {
        combined = new MediaStream([
          ...displayStream.getVideoTracks(),
          ...dest.stream.getAudioTracks(),
        ]);
      }
      streamRef.current = combined;

      // Choose supported mime
      const candidates = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm",
        "video/mp4",
      ];
      const mimeType = candidates.find((c) => window.MediaRecorder.isTypeSupported(c)) || "";

      const recorder = new MediaRecorder(combined, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || "video/webm" });
        const url = URL.createObjectURL(blob);
        setDownloadUrl(url);
        const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
        const ext = mimeType.includes("mp4") ? "mp4" : "webm";
        setDownloadName(`nutramilo-screen-${stamp}.${ext}`);
        // Stop any remaining tracks / audio contexts
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        if (micStreamRef.current) micStreamRef.current.getTracks().forEach((t) => t.stop());
        if (musicStopRef.current) { try { musicStopRef.current(); } catch { /* ignore */ } }
        if (musicCtxRef.current) { try { musicCtxRef.current.close(); } catch { /* ignore */ } }
        streamRef.current = null;
        micStreamRef.current = null;
        musicCtxRef.current = null;
        musicStopRef.current = null;
        if (tickRef.current) clearInterval(tickRef.current);
      };

      // If user cancels the browser share via UI, auto-stop
      displayStream.getVideoTracks()[0].onended = () => {
        try { recorder.state === "recording" && recorder.stop(); } catch { /* ignore */ }
      };

      recorder.start(1000);
      startTimeRef.current = Date.now();
      setDuration(0);
      tickRef.current = setInterval(() => {
        setDuration((Date.now() - startTimeRef.current) / 1000);
      }, 500);
      setRecording(true);
    } catch (e) {
      setError(e?.message || String(e));
      toast.error(bg ? "Не може да стартира запис: " : "Cannot start recording: " + (e?.message || String(e)));
    }
  }

  function stopRecording() {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch { /* ignore */ }
    setRecording(false);
  }

  function reset() {
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setDownloadUrl(null);
    setDownloadName("");
    setDuration(0);
    chunksRef.current = [];
  }

  const btnBase = "inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold shadow-sm";
  const cardCls = isDark
    ? "bg-[hsl(222,47%,10%)] border-white/10 text-gray-100"
    : "bg-white border-stone-200 text-stone-900";

  return (
    <>
      {/* Floating trigger */}
      {!open && !recording && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="screen-recorder-open-btn"
          title={bg ? "Запиши екрана" : "Record screen"}
          className={`fixed bottom-4 left-4 z-[9998] ${btnBase} ${
            isDark ? "bg-fuchsia-600 hover:bg-fuchsia-500 text-white" : "bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
          }`}
        >
          <Camera className="w-4 h-4" />
          {bg ? "Запиши екрана" : "Record screen"}
        </button>
      )}

      {/* Recording indicator (persists across panel open/close) */}
      {recording && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="screen-recorder-live-chip"
          className={`fixed bottom-4 left-4 z-[9998] ${btnBase} bg-red-600 hover:bg-red-500 text-white animate-pulse`}
        >
          <span className="w-2 h-2 rounded-full bg-white" />
          REC · {fmtDuration(duration)}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed inset-0 z-[9999] bg-black/60 flex items-end sm:items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="screen-recorder-panel-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`relative w-full max-w-md rounded-2xl border shadow-2xl p-4 ${cardCls}`}
            data-testid="screen-recorder-panel"
          >
            <button
              onClick={() => setOpen(false)}
              aria-label="Затвори"
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-black/10"
              data-testid="screen-recorder-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <Camera className={`w-5 h-5 ${isDark ? "text-fuchsia-300" : "text-fuchsia-600"}`} />
              <h3 className="text-lg font-extrabold">
                {bg ? "Запис на екрана" : "Screen recorder"}
              </h3>
            </div>

            {!isSupported && (
              <div className={`p-2.5 rounded-lg text-xs flex items-start gap-2 mb-3 ${
                isDark ? "bg-amber-500/10 text-amber-200" : "bg-amber-50 text-amber-900"
              }`}>
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  {bg
                    ? "Браузърът Ви не поддържа screen recording. Използвайте най-новата версия на Chrome/Edge/Firefox."
                    : "Your browser doesn't support screen recording. Use latest Chrome/Edge/Firefox."}
                </div>
              </div>
            )}

            {!recording && !downloadUrl && (
              <>
                <p className="text-xs opacity-70 mb-3 leading-relaxed">
                  {bg
                    ? "Записва избран прозорец / tab / целия екран. Файлът остава само локално — нищо не се качва на сървъра."
                    : "Records a chosen window/tab/screen. Stays local — nothing uploads to the server."}
                </p>
                <label className="flex items-center gap-2 text-xs mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={withMic}
                    onChange={(e) => setWithMic(e.target.checked)}
                    data-testid="screen-recorder-mic-toggle"
                    className="w-4 h-4"
                  />
                  {withMic ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5 opacity-60" />}
                  {bg ? "Записвай и микрофона" : "Also record microphone"}
                </label>
                <label className="flex items-center gap-2 text-xs mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={withMusic}
                    onChange={(e) => setWithMusic(e.target.checked)}
                    data-testid="screen-recorder-music-toggle"
                    className="w-4 h-4"
                  />
                  <span className="text-base">🎵</span>
                  {bg ? "Добави лека фонова мелодия" : "Add soft background music"}
                </label>
                <button
                  onClick={startRecording}
                  disabled={!isSupported}
                  data-testid="screen-recorder-start-btn"
                  className={`${btnBase} w-full justify-center bg-red-600 hover:bg-red-500 text-white disabled:opacity-50`}
                >
                  <Camera className="w-4 h-4" />
                  {bg ? "Започни запис" : "Start recording"}
                </button>
              </>
            )}

            {recording && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-red-600 animate-pulse" />
                  <div className="text-sm font-bold">{bg ? "Записва..." : "Recording..."}</div>
                  <div className="ml-auto font-mono text-lg font-black">{fmtDuration(duration)}</div>
                </div>
                <button
                  onClick={stopRecording}
                  data-testid="screen-recorder-stop-btn"
                  className={`${btnBase} w-full justify-center bg-red-600 hover:bg-red-500 text-white`}
                >
                  <StopCircle className="w-4 h-4" />
                  {bg ? "Спри и запази" : "Stop & save"}
                </button>
              </>
            )}

            {downloadUrl && !recording && (
              <>
                <p className="text-sm mb-3">
                  {bg ? "Готово! Свали файла или започни нов запис." : "Done! Download or start a new recording."}
                </p>
                <video
                  src={downloadUrl}
                  controls
                  playsInline
                  className="w-full rounded-lg bg-black mb-3"
                  data-testid="screen-recorder-preview"
                />
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={downloadUrl}
                    download={downloadName}
                    data-testid="screen-recorder-download-link"
                    className={`${btnBase} justify-center bg-emerald-600 hover:bg-emerald-500 text-white`}
                  >
                    <Download className="w-4 h-4" />
                    {bg ? "Свали" : "Download"}
                  </a>
                  <button
                    onClick={reset}
                    data-testid="screen-recorder-new-btn"
                    className={`${btnBase} justify-center ${
                      isDark ? "bg-white/10 hover:bg-white/15 text-white" : "bg-stone-100 hover:bg-stone-200 text-stone-800"
                    }`}
                  >
                    <Camera className="w-4 h-4" />
                    {bg ? "Нов запис" : "New recording"}
                  </button>
                </div>
              </>
            )}

            {error && (
              <div className={`mt-3 p-2 rounded-lg text-[11px] ${isDark ? "bg-red-500/10 text-red-200" : "bg-red-50 text-red-700"}`}>
                {error}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
